import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { ButtonCondensed } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';
import { type TokenDef } from './types';

type TokenSuggestionsProps = {
  tokenDefs: TokenDef[];
  onSelect: (token: TokenDef, keyboard?: boolean) => void;
  onCancel: (keyboard?: boolean) => void;
};

export type TokenSuggestionsRef = {
  prevSuggestion: () => void;
  nextSuggestion: () => void;
  selectSuggestion: () => void;
  cancelSuggestions: () => void;
};

const Wrapper = styled.div`
  position: absolute;
  top: -235px;
  left: 16px;
  right: 0;
  width: calc(100% - var(--cc_size_spacing_xl));
  height: 250px;
  display: flex;
  flex-direction: column;
  background-color: var(--cc_color_background_1);
  box-shadow: 0 var(--cc_size_depth_2_y) var(--cc_size_depth_2_blur)
    var(--cc_color_depth_2);
  border-radius: var(--cc_size_border_radius_l);
`;

const ScrollWrapper = styled.div`
  scroll-padding-top: var(--cc_size_spacing_xs);
  scroll-padding-bottom: var(--cc_size_spacing_xs);
  overflow-x: hidden;
  overflow-y: scroll;
  flex: 1;
`;

const CancelButton = styled.div`
  border-radius: var(--cc_size_border_radius_l) var(--cc_size_border_radius_l) 0
    0;
  display: flex;
  height: var(--cc_size_spacing_xl);
  background: var(--cc_color_background_2);
  align-items: center;
  justify-content: center;
  padding: var(--cc_size_spacing_s);
  cursor: pointer;
  transition: all ease-in-out 250ms;

  &:focus {
    outline: none;
  }
`;

const Suggestion = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: var(--cc_size_spacing_xs);
  margin: var(--cc_size_spacing_xs);
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  border-radius: var(--cc_size_border_radius_l);
  color: ${({ $isDisabled }) =>
    $isDisabled
      ? 'color-mix(in srgb, var(--cc_color_text_subtle), transparent 28%)'
      : 'var(--cc_color_text_default)'};
  font-weight: 400;
  font-size: var(--cc_size_text_s);
  background: ${({ $isActive }) =>
    $isActive ? 'var(--cc_color_background_3)' : 'transparent'};
`;

const ImageWrapper = styled.div`
  flex: none;
  width: var(--cc_size_avatar_s);
  height: var(--cc_size_avatar_s);
  margin-right: var(--cc_size_spacing_s);
  border-radius: 50%;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TextWrapper = styled.div`
  flex: 1;
`;

const Text = styled.span`
  display: block;
`;

const MetaText = styled.span`
  color: color-mix(in srgb, var(--cc_color_text_subtle), transparent 38%);
  font-size: var(--cc_size_text_xs);
`;

const NoResults = styled.p`
  display: block;
  padding: var(--cc_size_spacing_m);
  color: var(--cc_color_text_default);
  font-size: var(--cc_size_text_s);
  font-weight: 400;
`;

const getNextValidTokenPosition = (params: {
  tokenDefs?: TokenDef[];
  currentIndex: number;
  direction: 1 | -1 | 0;
}): number => {
  const { tokenDefs = [], currentIndex, direction } = params;
  const len = tokenDefs?.length ?? 0;

  // If direction is 0, find the nearest valid token starting from the current index
  if (direction === 0) {
    if (!tokenDefs[currentIndex]?.disabled) {
      return currentIndex;
    }
    // Search forward for the next valid token
    for (let i = currentIndex + 1; i < len; i += 1) {
      if (!tokenDefs[i].disabled) {
        return i;
      }
    }
    // If not found, search backward for the next valid token
    for (let i = currentIndex - 1; i >= 0; i -= 1) {
      if (!tokenDefs[i].disabled) {
        return i;
      }
    }
    // If no valid token is found, return the current index
    return currentIndex;
  }

  // If direction is not 0, find the next valid token in the given direction
  let newIndex = currentIndex;
  do {
    // The modulo operation ensures that the index wraps around the array.
    // Adding `len` before taking `% len` prevents negative indices when moving backward.
    newIndex = (newIndex + direction + len) % len;
  } while (tokenDefs[newIndex]?.disabled && newIndex !== currentIndex);

  return newIndex;
};

export const TokenSuggestions = forwardRef<
  TokenSuggestionsRef,
  TokenSuggestionsProps
>(({ tokenDefs, onSelect, onCancel }, ref) => {
  const [mode, setMode] = useState<'manual' | 'mouse'>('mouse');
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(
    getNextValidTokenPosition({ tokenDefs, currentIndex: 0, direction: 0 }),
  );

  const scrollTimeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectionRefs = useRef<HTMLDivElement[]>([]);

  const handleMouseMove = () => {
    if (mode !== 'mouse') setMode('mouse');
  };

  const changeSuggestion = useCallback(
    (direction: 1 | -1) => {
      setActiveSuggestionIndex((currentIndex) => {
        return getNextValidTokenPosition({
          tokenDefs,
          direction,
          currentIndex,
        });
      });
    },
    [tokenDefs],
  );

  useImperativeHandle(
    ref,
    () => ({
      nextSuggestion: () => {
        setMode('manual');
        changeSuggestion(1);
      },
      prevSuggestion: () => {
        setMode('manual');
        changeSuggestion(-1);
      },
      selectSuggestion: () => {
        setMode('manual');
        if (tokenDefs[activeSuggestionIndex]) {
          if (tokenDefs[activeSuggestionIndex].disabled) {
            return;
          }
          onSelect(tokenDefs[activeSuggestionIndex], true);
        } else {
          // select is invoked when there are no suggestions. assume cancel.
          onCancel(true);
        }
      },
      cancelSuggestions: () => {
        setMode('manual');
        onCancel(true);
      },
    }),
    [onCancel, changeSuggestion, onSelect, tokenDefs, activeSuggestionIndex],
  );

  useEffect(() => {
    setActiveSuggestionIndex(
      getNextValidTokenPosition({ tokenDefs, currentIndex: 0, direction: 0 }),
    );

    scrollTimeoutRefs.current.push(
      setTimeout(() => {
        selectionRefs.current?.[0]?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 16),
    );
  }, [tokenDefs]);

  useEffect(() => {
    if (mode === 'manual') {
      scrollTimeoutRefs.current.push(
        setTimeout(() => {
          selectionRefs.current?.[activeSuggestionIndex]?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 16),
      );
    }
  }, [activeSuggestionIndex, mode]);

  useEffect(() => {
    return () => {
      scrollTimeoutRefs.current.forEach(clearTimeout);
      scrollTimeoutRefs.current = [];
    };
  }, []);

  return (
    <Wrapper
      ref={containerRef}
      data-testid="activity__comment-suggestions-name-wrapper"
      onMouseMove={handleMouseMove}
    >
      <CancelButton>
        <ButtonCondensed
          data-testid="activity__comment-cancel-button"
          size="small"
          color="subtle"
          icon={{
            name: 'close-circle',
            position: 'before',
          }}
          onClick={() => {
            onCancel(false);
          }}
        >
          {translate('common.cancel')}
        </ButtonCondensed>
      </CancelButton>
      <ScrollWrapper>
        {tokenDefs.map((token, index) => {
          const suggestionText = token.text.trim();
          const isActive = index === activeSuggestionIndex;
          const isEnabled = !token.disabled;
          const isDisabled = token.disabled;

          return (
            <Suggestion
              data-testid={`activity__comment-suggestions-${index}${
                isActive ? '-active' : ''
              }`}
              ref={(e) => {
                selectionRefs.current[index] = e;
              }}
              key={`${token.type}:${token.value}:${suggestionText}`}
              $isActive={isActive ? isEnabled : false} // disabled tokens cannot be active
              $isDisabled={token.disabled}
              onMouseMove={
                activeSuggestionIndex !== index
                  ? () => {
                      if (isEnabled && mode === 'mouse') {
                        setActiveSuggestionIndex(index);
                      }
                    }
                  : undefined
              }
              onMouseEnter={() => {
                if (isEnabled && mode === 'mouse') {
                  setActiveSuggestionIndex(index);
                }
              }}
              onClick={() => {
                if (isEnabled) {
                  onSelect(token);
                }
              }}
            >
              <ImageWrapper>
                <Image
                  data-testid="activity__comment-image-avatar"
                  src={token.imageURL || '/default-avatar.png'}
                  alt={suggestionText}
                  onError={(e) => {
                    // prevents infinite request loop if default-avatar is also an error
                    if (e.currentTarget.src !== '/default-avatar.png') {
                      e.currentTarget.src = '/default-avatar.png';
                    }
                  }}
                />
              </ImageWrapper>
              <TextWrapper>
                <Text>{suggestionText}</Text>
                {isDisabled ? (
                  <MetaText>{token.disabledReason}</MetaText>
                ) : null}
              </TextWrapper>
            </Suggestion>
          );
        })}

        {tokenDefs.length === 0 ? (
          <NoResults data-testid="comment__no-results-text">
            {translate('activity.tokenSuggestions.noMatchingResults')}
          </NoResults>
        ) : null}
      </ScrollWrapper>
    </Wrapper>
  );
});
